import CheckIcon from '@material-ui/icons/Check';
import GetAppIcon from '@material-ui/icons/GetApp';
import { Elements, Loader, UploadFile } from '@tfo/common';
import fileDownload from 'js-file-download';
import React, { useState } from "react";
import { useIntl } from "react-intl";
import $http from "../../../../utils/$http";
import { FileType, FileViewModel } from '../../ViewModels';
import './Attachments.scss';

const { Paragraph } = Elements
const { Dropzone } = UploadFile

export type RequireAttachmentsProps = {
    orderId: number
    files: FileViewModel[]
    canAttachFile: boolean
    highlighted?: boolean
    onAttachmentsChange?: (attachments: Array<string>) => void
}

type AttachmentState = "completed" | "progress" | "failed"
type Attachment = {
    key: number
    file: FileViewModel
    state: AttachmentState
}

const Attachments = (props: RequireAttachmentsProps) => {
    const [attachments, setAttachments] = React.useState<Attachment[]>((props.files || [])
        .filter((item) => item.type === FileType.Attachment)
        .map(x => ({
            key: Math.random(),
            file: x, 
            state: 'completed'
        })))

    const setUploadState = (fileName: string, state: AttachmentState) => {
        setAttachments(attachments => {
            return [...attachments, {
                key: Math.random(),
                file: {
                    fileName: fileName,
                    type: FileType.Attachment,
                    downloadUrlPath: ''
                }, 
                state 
            }]
        })
    }

    return (
        <div className="require-attachments">
            <AttachmentDescriptionParagraph />
            <UploadAttachments {...props}
                uploadComplete={f => setUploadState(f, "completed")} />
            <AttachedFiles attachments={attachments} />
        </div>
    )
}

const AttachedFiles = ({ attachments }: {
    attachments: Attachment[]
}) => {
    const intl = useIntl()

    const [isDownloading, setIsDownloading] = useState<boolean>(false)
    const downloadAuthorizedFile = (e: React.MouseEvent, file: FileViewModel) => {
        e.preventDefault()
        setIsDownloading(true)
        $http.get<Blob>(file.downloadUrlPath, 'blob')
            .then(data => fileDownload(data, file.fileName))
            .finally(() => setIsDownloading(false))
    }

    const hasAttachments = attachments && attachments.length > 0

    return !hasAttachments
        ? null
        : <>
            <Paragraph className='attached-files-list-header'>
                {attachments.length > 1
                    ? intl.formatMessage({ id: 'requireAttachments.fileList.header.plural' })
                    : intl.formatMessage({ id: 'requireAttachments.fileList.header.singular' })}
            </Paragraph>
            <ul className='attached-files-list'>
                {attachments.map(attachment =>
                    <li key={attachment.key} className='download-file-link'>
                        {attachment.file.downloadUrlPath &&
                            <a href={attachment.file.downloadUrlPath} onClick={(e) => downloadAuthorizedFile(e, attachment.file)} >
                                <GetAppIcon /> {attachment.file.fileName}
                            </a>}
                        {!attachment.file.downloadUrlPath && 
                            <span>
                                <CheckIcon /> {attachment.file.fileName}
                            </span>}
                    </li>)}
            </ul>
            {isDownloading && <Loader />}
        </>
}

const AttachmentDescriptionParagraph = () => {
    const intl = useIntl()

    return (<>
        <Paragraph className='description-note' >
            {intl.formatMessage(
                { id: 'requireAttachments.attach.note' },
                {
                    link: (
                        <a href={intl.formatMessage({ id: 'requireAttachments.attach.note.link-url' })}
                            target='_blank'
                            rel="noreferrer">
                            {intl.formatMessage({ id: 'requireAttachments.attach.note.link-text' })}
                        </a>
                    ),
                },
            )}
        </Paragraph>
    </>)
}

const UploadAttachments = ({ orderId, ...events }: RequireAttachmentsProps & {
    uploadComplete: (fileName: string) => void,
}) => {
    const intl = useIntl()
    const [submitError, setSubmitError] = React.useState<number | null>(null);
    const [isLoading, setIsLoading] = React.useState(false);

    const saveAttachment = async (file: File) => {
        setIsLoading(true)
        setSubmitError(null)
        try {
            const formData = new FormData()
            formData.append('file', file)

            await $http.put(
                `attach/${orderId}`,
                formData,
                null, //no response content type expected
                'multipart/form-data')

            events.uploadComplete(file.name)

            const attachments = [file.name]
            //Default to no-op if onAttachmentsChange has not been set as it's optional
            const onChange = events.onAttachmentsChange || (() =>{}) 
            onChange(attachments)
        }
        catch (e) {
            console.error(e)

            let error = e as {
                response: {
                    status: number
                }
            }

            if (error.response === undefined) {
                setSubmitError(999)
            }

            else if (error.response.status === 413) {
                setSubmitError(413)
            }

            else {
                setSubmitError(500)
            }
        }
        finally {
            setIsLoading(false)
        }
    }

    return (<>
        <div className={`attachments-drop-zone ${events.highlighted ? 'highlighted' : ''}`}>
            <Dropzone
                files={[]}
                onFilesAdded={(files) => saveAttachment(files[0])}
                submitError={!!submitError}
                disabled={false}
                single={true}
                hideSubText={true}
            />
            {submitError &&
                <>
                    <Paragraph className='upload-fail-message' small >
                        {intl.formatMessage({ id: `requireAttachments.attach.upload-fail-${submitError}` })}
                    </Paragraph>
                </>
            }
        </div>
        {isLoading && <Loader />}
    </>);
}

export default Attachments